import {
  action,
  computed,
  IReactionDisposer,
  makeObservable,
  observable,
  reaction,
  runInAction,
} from "mobx";

import type { Uuid } from "@/domains/global/identifiers";
import { CacheKey } from "@/modules/cache/constants";
import { LEGACY_GLOBAL_CACHE_INSTANCE } from "@/modules/cache/globalCache";
import { audioRecordingModule } from "@/modules/audio-recording";
import { TranscriptionStatus } from "@/store/audio/recording";
import type { AppSubStoreArgs } from "@/store/types";
import { AppSubStore } from "@/store/types";
import { AppStoreAudioRecordingStore } from "@/store/audio/recording/audio-recording.store";
import {
  AppStoreAudioTransformingStore,
  AudioRecordingTransformingState,
} from "@/store/audio/transforming";
import { AppStoreAudioUiStore } from "@/store/audio/ui/audio-ui.store";
import { AppStoreAudioUploadingStore, AudioRecordingUploadingState } from "@/store/audio/uploading";
import type { IAudioStore } from "@/store/audio/audio.types";
import { AudioRecorderLockHandle } from "@/modules/audio-recording/audio-recorder-lock";
import { logger } from "@/modules/logger";
import { AudioRecorderLockingError } from "@/domains/errors/kinds/contention";
import { objectModule } from "@/modules/object";
import { toastModule } from "@/modules/toast";
import { AudioRecordingObservable } from "@/store/audio-recordings";
import { TrackedEvent } from "@/domains/metrics/tracked-event";
import { trackEvent } from "@/domains/metrics";
import { CustomEventName, dispatchCustomEvent } from "@/modules/custom-event";
import { clientEnvModule } from "@/modules/client-env";
import { PreparedRecording } from "@/store/audio/recording/prepared-recording-controller";
import { DateTime } from "luxon";

type RecordingLockAction = {
  audioRecordingId: Uuid;
  action: () => Promise<void>;
  toastMessage: string;
};

type CreateAudioRecordingArgs = {
  audioRecordingId?: Uuid;
  preparedRecording?: PreparedRecording;
  expandVoicePopover?: boolean;
  associatedNoteId?: Uuid | null;
  associatedMeetingWindowId?: string;
  config?: {
    showOnboardingTooltip: boolean;
    forceWebMediaController?: boolean;
  };
};

export class AppStoreAudioStore extends AppSubStore implements IAudioStore {
  ui: AppStoreAudioUiStore;
  recording: AppStoreAudioRecordingStore;
  uploading: AppStoreAudioUploadingStore;
  transforming: AppStoreAudioTransformingStore;

  private activeRecordingLockHandle: AudioRecorderLockHandle | null = null;
  private activeRecordingIdValue: Uuid | null = null;
  private deactivateOrphanedActiveRecordingReaction: IReactionDisposer;
  private readonly preparedRecordingsMap = new Map<Uuid, PreparedRecording>();

  constructor(injectedDeps: AppSubStoreArgs) {
    super(injectedDeps);

    this.ui = new AppStoreAudioUiStore(injectedDeps);
    this.ui.initialize();

    this.transforming = new AppStoreAudioTransformingStore({
      ...injectedDeps,
    });

    this.uploading = new AppStoreAudioUploadingStore({
      ...injectedDeps,
      config: {
        onUploadComplete: ({ audioRecordingId }) => {
          void this.transforming.enqueue({ audioRecordingId });
        },
      },
    });

    this.recording = new AppStoreAudioRecordingStore({
      ...injectedDeps,
      uiStore: this.ui,
      config: {
        onRecordingCompleted: ({ audioRecordingId }) => {
          void this.uploading.enqueue({ audioRecordingId });
        },
      },
    });

    makeObservable<
      this,
      | "activeRecordingLockHandle"
      | "withRecordingTabLock"
      | "obtainAudioRecordingLock"
      | "releaseAudioRecordingLock"
      | "setActiveAudioRecording"
      | "clearActiveAudioRecording"
      | "activeRecordingIdValue"
      | "hasLockInCurrentTab"
      | "maybeObtainAudioRecordingLock"
      | "deactivateOrphanedActiveRecordingReaction"
      | "shouldBlockRecordingStart"
      | "preparedRecordingsMap"
    >(this, {
      recording: observable,
      uploading: observable,
      transforming: observable,
      ui: observable,
      activeRecordingLockHandle: observable,
      activeRecordingId: computed,
      activeRecordingNoteId: computed,
      activeRecording: computed,
      hasActiveRecording: computed,
      createAndStartAudioRecording: action,
      prepareRecording: action,
      pauseRecording: action,
      resumeRecording: action,
      finishRecording: action,
      trashRecording: action,
      startUploading: action,
      startTransforming: action,
      initialize: action,
      withRecordingTabLock: action,
      obtainAudioRecordingLock: action,
      releaseAudioRecordingLock: action,
      setActiveAudioRecording: action,
      clearActiveAudioRecording: action,
      getUploadStateForRecording: false,
      getTransformStateForRecording: false,
      _activateRecording: action,
      _deactivateRecording: action,
      dispose: action,
      resetState: action,
      rehydrateAndObtainAudioRecordingLockIfNecessary: action,
      maybeObtainAudioRecordingLock: action,
      activeRecordingIdValue: observable,
      setAcknowledgedTransformRemoteUpdate: action,
      hasLockInCurrentTab: computed,
      deactivateOrphanedActiveRecordingReaction: observable.ref,
      ensureActiveRecordingStateIsValid: action,
      shouldBlockRecordingStart: action,
      preparedRecordingsMap: false,
      getPreparedRecordingById: false,
      getPreparedRecording: false,
    });

    /**
     * If our audio recording becomes orphaned, then we automatically trash it.
     */
    this.deactivateOrphanedActiveRecordingReaction = reaction(
      () => {
        if (!this.hasLockInCurrentTab) {
          return { shouldDeactivate: false };
        }

        const activeRecordingId = this.activeRecordingIdValue;
        if (!activeRecordingId) {
          return { shouldDeactivate: false };
        }

        const recording = this.store.audioRecordings.get(activeRecordingId);
        if (!recording) {
          return { shouldDeactivate: false };
        }

        /**
         * If the audio recording has no associatedNoteId, then that is alright.
         * (Note that this kind of state is currently only used for internal/testing purposes)
         */
        const noteId = recording.associatedNoteId;
        if (!noteId) {
          return { shouldDeactivate: false };
        }

        const note = this.store.notes.getNoteObservableById({ noteId });
        const doesNotExist = this.store.notes.doesNotExist(noteId);

        const isTrashedOrDeleted = note ? note.isTrashed || note.isDeleted : false;

        return {
          audioRecordingId: activeRecordingId,
          shouldDeactivate: isTrashedOrDeleted || doesNotExist,
        };
      },
      async ({ audioRecordingId, shouldDeactivate }, _prev) => {
        if (!audioRecordingId) return;
        if (!shouldDeactivate) return;

        try {
          await this.trashRecording({ audioRecordingId });
        } catch (unknownErr) {
          logger.warn({
            message:
              "[AppStoreAudioStore] failed to deactivate orphaned active recording after note removal",
            info: { error: objectModule.safeErrorAsJson(unknownErr as Error) },
          });
        }
      }
    );
  }

  getPreparedRecordingById(audioRecordingId: Uuid): PreparedRecording | undefined {
    const preparedRecording = this.preparedRecordingsMap.get(audioRecordingId);
    if (!preparedRecording) return undefined;
    if (preparedRecording.expiresAt < DateTime.local()) {
      this.preparedRecordingsMap.delete(audioRecordingId);
      return undefined;
    }
    return preparedRecording;
  }

  getPreparedRecording(): PreparedRecording | undefined {
    if (this.activeRecordingId) return;

    const preparedRecording = this.recording.getPreparedRecording();
    this.preparedRecordingsMap.set(preparedRecording.audioRecordingId, preparedRecording);
    return preparedRecording;
  }

  get activeRecordingId(): Uuid | null {
    if (!this.hasLockInCurrentTab) return null;

    return this.activeRecordingIdValue ?? null;
  }

  get activeRecording(): AudioRecordingObservable | null {
    if (!this.hasLockInCurrentTab) return null;

    const activeId = this.activeRecordingIdValue;
    if (!activeId) return null;

    return this.store.audioRecordings.get(activeId) ?? null;
  }

  get activeRecordingNoteId(): Uuid | null {
    const recording = this.activeRecording;
    if (!recording) return null;

    return (recording.associatedNoteId as Uuid | undefined) ?? null;
  }

  get hasActiveRecording(): boolean {
    return this.activeRecordingId !== null;
  }

  async createAndStartAudioRecording({
    preparedRecording: providedPreparedRecording,
    audioRecordingId: providedAudioRecordingId,
    expandVoicePopover = true,
    associatedNoteId,
    associatedMeetingWindowId,
    config,
  }: CreateAudioRecordingArgs): Promise<void> {
    if (clientEnvModule.isMobileBrowser()) {
      toastModule.triggerToast({
        content: "Voice recording is not available on mobile browsers",
        toastId: "voice-recording-not-available-on-mobile-browsers",
      });
      return;
    }

    if (!this.store.systemPermissions.hasRequiredVoicePermissions) {
      this.store.systemPermissions.ui.openVoicePermissionsModal();
      return;
    }

    if (!providedPreparedRecording) {
      this.store.electron.pushRecallDebugEvent({}, "in-app-voice-recording");
    }
    const preparedRecording =
      // If a prepared recording is provided, use it.
      providedPreparedRecording ??
      // If an audio recording id is provided, try to find a prepared recording.
      (providedAudioRecordingId
        ? this.preparedRecordingsMap.get(providedAudioRecordingId)
        : undefined) ??
      // If no prepared recording is found, create a new one.
      this.recording.getPreparedRecording(providedAudioRecordingId);

    const audioRecordingId = preparedRecording.audioRecordingId;
    this.preparedRecordingsMap.set(audioRecordingId, preparedRecording);

    await this.withRecordingTabLock({
      audioRecordingId,
      action: async () => {
        if (this.shouldBlockRecordingStart({ audioRecordingId })) {
          const activeRecordingId = this.activeRecordingIdValue;
          if (!activeRecordingId) return;

          this.ui.promptToReplaceActiveRecording({
            onConfirm: async () => {
              await this.finishRecording({ audioRecordingId: activeRecordingId });

              await this.createAndStartAudioRecording({
                preparedRecording,
                associatedNoteId,
                associatedMeetingWindowId,
                config,
              });
            },
          });

          return;
        }

        if (config?.showOnboardingTooltip) {
          this.ui.setShowOnboardingTooltip(true);
        }

        logger.info({
          message: "[AppStoreAudioStore] createAndStartAudioRecording",
          info: { associatedMeetingWindowId: associatedMeetingWindowId ?? null },
        });

        const audioRecording = await this.store.dexie.repositories.audioRecordings.upsert({
          id: audioRecordingId,
          data: {
            associatedNoteId: associatedNoteId ?? null,
          },
        });

        await this.prepareRecording({ audioRecordingId: audioRecording.id });

        await this.setActiveAudioRecording({ audioRecordingId });

        await this.recording.start({
          preparedRecording,
          detectedMeetingWindowId: associatedMeetingWindowId,
          forceWebMediaController: config?.forceWebMediaController ?? false,
        });

        if (expandVoicePopover && associatedNoteId) {
          dispatchCustomEvent(CustomEventName.ExpandVoicePopover, {
            noteId: associatedNoteId,
          });
        }

        trackEvent(TrackedEvent.VoiceSessionStart);
      },
      toastMessage: "Unable to start - Mem is recording on a different tab.",
    });
  }

  async prepareRecording({ audioRecordingId }: { audioRecordingId: Uuid }): Promise<void> {
    this.ui.resetRecording();
    this.recording.isPaused = false;
    this.recording.waveformSamples = null;
    this.recording.setMicrophoneState({ label: null, isDisconnected: false });
    this.recording.setVisualizer(null);
    this.recording.setTranscriptionStatus(audioRecordingId, TranscriptionStatus.Live);
    this.recording.setTranscriptionUnsupported(false);
  }

  async pauseRecording({ audioRecordingId }: { audioRecordingId: Uuid }): Promise<void> {
    /**
     * Note that we don't release the lock here.
     * We only release the lock when the recording is stopped (either by finishing or by trashing).
     */
    try {
      await this.recording.pause({ audioRecordingId });
      trackEvent(TrackedEvent.VoiceSessionPause);
    } catch (unknownErr) {
      toastModule.triggerToast({
        content: "Unable to pause recording",
        toastId: audioRecordingId,
      });

      logger.warn({
        message: "[AppStoreAudioStore] pauseRecording: failed to pause recording",
        info: {
          audioRecordingId,
          error: objectModule.safeErrorAsJson(unknownErr as Error),
        },
      });
    }
  }

  async resumeRecording({
    audioRecordingId,
    preparedRecording: providedPreparedRecording,
  }: {
    audioRecordingId: Uuid;
    preparedRecording?: PreparedRecording;
  }): Promise<void> {
    if (!this.store.systemPermissions.hasRequiredVoicePermissions) {
      this.store.systemPermissions.ui.openVoicePermissionsModal();
      return;
    }

    const preparedRecording =
      providedPreparedRecording ??
      this.preparedRecordingsMap.get(audioRecordingId) ??
      this.recording.getPreparedRecording(audioRecordingId);

    this.preparedRecordingsMap.set(audioRecordingId, preparedRecording);

    await this.withRecordingTabLock({
      audioRecordingId,
      action: async () => {
        if (this.shouldBlockRecordingStart({ audioRecordingId })) {
          const activeRecordingId = this.activeRecordingIdValue;
          if (!activeRecordingId) return;

          this.ui.promptToReplaceActiveRecording({
            onConfirm: async () => {
              await this.finishRecording({ audioRecordingId: activeRecordingId });

              await this.resumeRecording({
                audioRecordingId,
                preparedRecording,
              });
            },
          });

          return;
        }

        /** Should be a no-op but included for completeness */
        await this.setActiveAudioRecording({ audioRecordingId });

        await this.recording.resume({ preparedRecording });
        trackEvent(TrackedEvent.VoiceSessionResume);
      },
      toastMessage: "Unable to resume - Mem is recording on a different tab.",
    });
  }

  private shouldBlockRecordingStart({
    audioRecordingId: requestedRecordingId,
  }: {
    audioRecordingId: Uuid;
  }): boolean {
    if (!this.hasActiveRecording) return false;

    const activeRecordingId = this.activeRecordingIdValue;
    if (!activeRecordingId) return false;

    return requestedRecordingId !== activeRecordingId;
  }

  async finishRecording({
    audioRecordingId,
    detectedMeetingWindowId,
  }: {
    audioRecordingId: Uuid;
    detectedMeetingWindowId?: string;
  }): Promise<void> {
    await this.withRecordingTabLock({
      audioRecordingId,
      action: async () => {
        /**
         * Note that we aren't currently using the `detectedMeetingWindowId` here, we just
         * include it for logging purposes.
         */
        logger.info({
          message: "[AppStoreAudioStore] finishRecording",
          info: { audioRecordingId, detectedMeetingWindowId: detectedMeetingWindowId ?? null },
        });

        await this.recording.stop({ audioRecordingId });
        trackEvent(TrackedEvent.VoiceSessionDone);

        await this.clearActiveAudioRecording({
          audioRecordingId,
        });
        await this.releaseAudioRecordingLock({ audioRecordingId });
      },
      toastMessage: "Unable to save this recording - Mem is recording on a different tab.",
    });
  }

  async trashRecording({ audioRecordingId }: { audioRecordingId: Uuid }): Promise<void> {
    await this.withRecordingTabLock({
      audioRecordingId,
      action: async () => {
        await this.recording.clear({ audioRecordingId });
        await this.store.dexie.repositories.audioRecordings.trash({ id: audioRecordingId });

        await this.clearActiveAudioRecording({
          audioRecordingId,
        });
        await this.releaseAudioRecordingLock({ audioRecordingId });
      },
      toastMessage: "Unable to delete this recording - Mem is recording on a different tab.",
    });
  }

  async startUploading({ audioRecordingId }: { audioRecordingId: Uuid }): Promise<void> {
    await this.uploading.enqueue({ audioRecordingId });
  }

  async startTransforming({ audioRecordingId }: { audioRecordingId: Uuid }): Promise<void> {
    await this.transforming.enqueue({ audioRecordingId });
  }

  getUploadStateForRecording({
    audioRecordingId,
  }: {
    audioRecordingId: Uuid;
  }): AudioRecordingUploadingState {
    return this.uploading.getProgress({ audioRecordingId });
  }

  getTransformStateForRecording({
    audioRecordingId,
  }: {
    audioRecordingId: Uuid;
  }): AudioRecordingTransformingState {
    return this.transforming.getTransformState({ audioRecordingId });
  }

  setAcknowledgedTransformRemoteUpdate({ audioRecordingId }: { audioRecordingId: Uuid }): void {
    this.transforming.setTransformState({
      audioRecordingId,
      state: {
        ...this.transforming.getTransformState({ audioRecordingId }),
        awaitingRemoteUpdate: false,
      },
    });
  }

  private async withRecordingTabLock({
    audioRecordingId,
    action,
    toastMessage,
  }: RecordingLockAction): Promise<void> {
    try {
      await this.obtainAudioRecordingLock();
      await action();
    } catch (unknownErr) {
      await this.clearActiveAudioRecording({
        audioRecordingId,
      });
      await this.releaseAudioRecordingLock({ audioRecordingId });

      if (unknownErr instanceof AudioRecorderLockingError) {
        toastModule.triggerToast({
          content: toastMessage,
          toastId: audioRecordingId,
        });

        return;
      }

      logger.warn({
        message: "[AppStoreAudioStore] recording action failed",
        info: {
          audioRecordingId,
          error: objectModule.safeErrorAsJson(unknownErr as Error),
        },
      });

      throw unknownErr;
    }
  }

  private async setActiveAudioRecording({
    audioRecordingId,
  }: {
    audioRecordingId: Uuid;
  }): Promise<void> {
    await this.store.globalCache.set(CacheKey.ActiveAudioRecording, { audioRecordingId });
    runInAction(() => {
      this.activeRecordingIdValue = audioRecordingId;
    });
  }

  private async clearActiveAudioRecording({
    audioRecordingId,
  }: {
    audioRecordingId?: Uuid | null;
  }): Promise<void> {
    /**
     * If a value is provided...
     * Then we perform a check to ensure that the value is the same as the active recording id.
     */
    if (audioRecordingId && this.activeRecordingIdValue) {
      if (audioRecordingId !== this.activeRecordingIdValue) {
        logger.info({
          message: "[AppStoreAudioStore] clearActiveAudioRecording: active recording id mismatch",
          info: {
            audioRecordingId,
            activeRecordingId: this.activeRecordingIdValue,
          },
        });

        return;
      }
    }

    const ownsLock = this.hasLockInCurrentTab;

    if (ownsLock) {
      await this.store.globalCache.del(CacheKey.ActiveAudioRecording);
    }
    runInAction(() => {
      this.activeRecordingIdValue = null;
    });
  }

  private async obtainAudioRecordingLock(): Promise<void> {
    if (this.activeRecordingLockHandle) {
      logger.info({
        message: "[AppStoreAudioStore] obtainAudioRecordingLock: already locked",
      });

      return;
    }

    const lockHandle = await audioRecordingModule.takeLock();
    runInAction(() => {
      this.activeRecordingLockHandle = lockHandle;
    });
  }

  private async maybeObtainAudioRecordingLock(): Promise<void> {
    try {
      await this.obtainAudioRecordingLock();
    } catch (unknownErr) {
      if (unknownErr instanceof AudioRecorderLockingError) {
        return;
      }

      logger.warn({
        message: "[AppStoreAudioStore] maybeObtainAudioRecordingLock: failed to obtain lock",
        info: { error: objectModule.safeErrorAsJson(unknownErr as Error) },
      });

      throw unknownErr;
    }
  }

  private async releaseAudioRecordingLock({
    audioRecordingId,
  }: {
    audioRecordingId?: Uuid | null;
  }): Promise<void> {
    /**
     * If a value is provided...
     * Then we perform a check to ensure that the value is the same as the active recording id.
     */
    if (audioRecordingId && this.activeRecordingIdValue) {
      if (audioRecordingId !== this.activeRecordingIdValue) {
        logger.info({
          message: "[AppStoreAudioStore] releaseAudioRecordingLock: active recording id mismatch",
          info: {
            audioRecordingId,
            activeRecordingId: this.activeRecordingIdValue,
          },
        });

        return;
      }
    }

    if (!this.activeRecordingLockHandle) {
      logger.info({
        message: "[AppStoreAudioStore] releaseAudioRecordingLock: no lock to release",
      });

      return;
    }

    await audioRecordingModule.releaseLock(this.activeRecordingLockHandle);
    runInAction(() => {
      this.activeRecordingLockHandle = null;
    });
  }

  async initialize(): Promise<void> {
    await this.rehydrateAndObtainAudioRecordingLockIfNecessary();
  }

  async rehydrateAndObtainAudioRecordingLockIfNecessary(): Promise<void> {
    let cachedData = await this.store.globalCache.get(CacheKey.ActiveAudioRecording);

    if (!cachedData) {
      // Try legacy cache for migration
      const legacyData = await LEGACY_GLOBAL_CACHE_INSTANCE.get(CacheKey.ActiveAudioRecording);
      if (legacyData) {
        await this.store.globalCache.set(CacheKey.ActiveAudioRecording, legacyData);

        // Clear legacy cache after migration
        await LEGACY_GLOBAL_CACHE_INSTANCE.del(CacheKey.ActiveAudioRecording);

        cachedData = legacyData;
      }
    }

    const cachedAudioRecordingId = cachedData?.audioRecordingId;
    if (!cachedAudioRecordingId) return;

    /**
     * We attempt to obtain the audio recording lock on-boot.
     *
     * The way it works is:
     * - Only one tab is allowed to access the "active recording" at a time.
     * - An active recording is defined as an audio-recording that is either recording or paused.
     * - The id is stored in the cache (dexie) and observed for changes.
     * - On boot, we start observing it.
     * - If it is in the active state (recording or paused) we obtain the lock.
     * - First tab to do this becomes the "primary" tab.
     */
    const isActivelyRecording =
      await this.store.dexie.repositories.audioRecordings.isInRecordingState({
        id: cachedAudioRecordingId,
      });

    if (!isActivelyRecording) {
      return;
    }

    try {
      await this.obtainAudioRecordingLock();
      await this.setActiveAudioRecording({ audioRecordingId: cachedAudioRecordingId });
    } catch (unknownErr) {
      if (unknownErr instanceof AudioRecorderLockingError) {
        return;
      }

      logger.warn({
        message:
          "[AppStoreAudioStore] rehydrateAndObtainAudioRecordingLockIfNecessary: failed to obtain lock",
        info: {
          error: objectModule.safeErrorAsJson(unknownErr as Error),
        },
      });

      throw unknownErr;
    }
  }

  async dispose(): Promise<void> {
    await this.releaseAudioRecordingLock({ audioRecordingId: null });
  }

  async resetState(): Promise<void> {
    await this.maybeObtainAudioRecordingLock();

    this.ui.resetState();
    this.recording.resetState();
    this.uploading.resetState();
    this.transforming.resetState();

    await this.clearActiveAudioRecording({ audioRecordingId: null });
    await this.releaseAudioRecordingLock({ audioRecordingId: null });
  }

  async ensureActiveRecordingStateIsValid(): Promise<void> {
    if (!this.activeRecordingId) return;

    /**
     * If we have an activeRecordingId, but no active recording on the device, we'll just
     * try resetting the state.
     *
     * The active recording should always be "in progress".
     * If it hasn't been started, or it has been completed, we'll clear it out.
     *
     * Note that - if a note doesn't have an `associatedNoteId`, this will also clear it out.
     * (This may become a valid state in the future, but for now it is always expected to be associated
     * with a note.)
     *
     * The recording should also always be from the current client device.
     */
    if (
      !this.activeRecording ||
      this.activeRecording.isTrashed ||
      !this.activeRecording.hasStartedRecording ||
      this.activeRecording.hasBeenCompleted ||
      !this.activeRecording.associatedNoteId ||
      !(await this.activeRecording.isFromCurrentClientDevice())
    ) {
      await this.resetState();
      return;
    }
  }

  async _activateRecording({ audioRecordingId }: { audioRecordingId: Uuid }): Promise<void> {
    await this.setActiveAudioRecording({ audioRecordingId });
    await this.obtainAudioRecordingLock();
  }

  async _deactivateRecording(): Promise<void> {
    const activeRecordingId = this.activeRecordingIdValue;
    await this.clearActiveAudioRecording({ audioRecordingId: activeRecordingId });
    await this.releaseAudioRecordingLock({ audioRecordingId: activeRecordingId });
  }

  private get hasLockInCurrentTab(): boolean {
    return this.activeRecordingLockHandle !== null;
  }
}
